import { Fragment } from 'react';
import dynamic from 'next/dynamic';
import { Portal } from '@headlessui/react';

import { Price } from '@/atoms';
import CartIcon from '@/molecules/cart-icon';

import { isExternal, isOutOfStock, whiteColorScheme } from '@/lib/utils';

import type { FunctionComponent, ReactNode } from 'react';
import type { ProductFormProps } from './types';

const AvisameButton = dynamic(() => import('../buttons/Avisame'));
const Ticket = dynamic(() =>
  import('@/icons/Ticket').then((mod) => mod.Ticket),
);

interface TypeProductFormMobileProps extends ProductFormProps {
  children: (
    children: (config?: { disabled: boolean }) => ReactNode,
  ) => ReactNode | ReactNode[];
}

/**
 * ProductFormMobile
 */
export const ProductFormMobile: FunctionComponent<
  TypeProductFormMobileProps
> = ({
  isSellerClosed,
  price,
  productId,
  quantitySelector,
  regularPrice,
  state,
  externalUrl,
  moreInfo,
  stock,
  stockAlert,
  type,
  children,
}: TypeProductFormMobileProps) => (
  <Portal as={Fragment}>
    <div className="fixed bottom-14 left-0 right-0 z-20 flex flex-row-reverse items-center justify-end gap-2 divide-x-[1px] divide-x-reverse border-t border-primary-200 bg-white px-3 py-2 lg:hidden">
      <p className="u-actions u-actions--link flex flex-col pl-2 text-typo-secondary">
        {moreInfo}
      </p>

      <Price
        isDescription
        regularPrice={regularPrice}
        price={price}
        variant="horizontal"
        className="pr-2"
      />
    </div>

    {isSellerClosed || (stock && isOutOfStock(stock)) ? (
      stockAlert ? (
        <AvisameButton
          product={productId}
          styled="mobile"
          className="fixed bottom-0 left-0 right-0 z-20 grid h-14 lg:hidden"
        />
      ) : null
    ) : type && isExternal(type) ? (
      <a
        href={externalUrl ?? '#'}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-0 left-0 right-0 z-20 flex h-14 flex-row items-center justify-center gap-2 bg-secondary-500 p-4 text-base text-typo-alternative disabled:bg-secondary-100 disabled:text-primary-200 lg:hidden"
      >
        Reservar <Ticket width={24} height={24} />
      </a>
    ) : (
      children(({ disabled } = { disabled: false }) => (
        <>
          {quantitySelector}

          <button
            type="submit"
            disabled={disabled || state === 'LOADING'}
            className="flex flex-row items-center justify-center gap-2 bg-secondary-500 p-4 text-base text-typo-alternative disabled:bg-secondary-100 disabled:text-primary-200"
          >
            Añadir
            <CartIcon
              width={24}
              height={24}
              state={state}
              colorScheme={whiteColorScheme}
              className="flex-shrink-0"
            />
          </button>
        </>
      ))
    )}
  </Portal>
);

ProductFormMobile.displayName = 'ProductFormMobile';
